
import React, { useState } from 'react';
import {
  LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell,
} from 'recharts';
import { Panel, Metric, LogPanel, timestamp } from '../../components/ui.jsx';

const AC = 'var(--cyan)';
import { _livePalette as P } from '../../context/paletteSync';

const Btn = ({onClick,children,color=AC,disabled,style}) => (
  <button onClick={onClick} disabled={disabled} style={{padding:'8px 16px',background:color+'22',border:`1px solid ${color}`,color,fontSize:'10px',cursor:disabled?'not-allowed':'pointer',borderRadius:'3px',fontFamily:'JetBrains Mono',fontWeight:700,letterSpacing:'.08em',opacity:disabled?.4:1,...style}}>{children}</button>
);
const Sl = ({label,value,min,max,step=1,onChange,unit='',color=AC}) => (
  <div style={{marginBottom:'10px'}}>
    <div style={{display:'flex',justifyContent:'space-between',marginBottom:'3px'}}>
      <span style={{fontSize:'10px',color:'var(--t2)'}}>{label}</span>
      <span style={{fontSize:'10px',color,fontWeight:700}}>{value}{unit}</span>
    </div>
    <div style={{position:'relative',height:'3px',background:'var(--b0)',borderRadius:'2px'}}>
      <div style={{position:'absolute',left:0,height:'100%',background:`linear-gradient(90deg,${color}44,${color})`,width:`${((value-min)/(max-min))*100}%`}}/>
      <input type="range" min={min} max={max} step={step} value={value} onChange={e=>onChange(+e.target.value)}
        style={{position:'absolute',inset:'-7px 0',width:'100%',opacity:0,cursor:'pointer',height:'18px'}}/>
    </div>
  </div>
);
const Sel = ({label,value,onChange,options}) => (
  <div style={{marginBottom:'8px'}}>
    {label&&<div style={{fontSize:'9px',color:'var(--t3)',letterSpacing:'.1em',marginBottom:'3px'}}>{label}</div>}
    <select value={value} onChange={e=>onChange(e.target.value)} style={{width:'100%',background:'var(--deep)',border:'1px solid var(--b1)',color:'var(--t1)',padding:'6px 8px',fontSize:'11px',fontFamily:'JetBrains Mono',borderRadius:'2px',outline:'none'}}>
      {options.map(o=><option key={o.value??o} value={o.value??o}>{o.label??o}</option>)}
    </select>
  </div>
);

// ─── MATH ─────────────────────────────────────────────────────────────────────
const randn = () => { let u=0,v=0; while(!u)u=Math.random(); while(!v)v=Math.random(); return Math.sqrt(-2*Math.log(u))*Math.cos(2*Math.PI*v); };
const softmax = arr => { const mx=Math.max(...arr); const e=arr.map(x=>Math.exp(x-mx)); const s=e.reduce((a,b)=>a+b,0); return e.map(v=>v/s); };
const matMul = (A,B) => A.map(row=>B[0].map((_,j)=>row.reduce((s,v,k)=>s+v*B[k][j],0)));
const dotV = (a,b) => a.reduce((s,v,i)=>s+v*b[i],0);

const selfAttention = (Q,K,V,dk) => {
  const scores = Q.map(q=>K.map(k=>dotV(q,k)/Math.sqrt(dk)));
  const weights = scores.map(row=>softmax(row));
  const out = weights.map(ws=>V[0].map((_,j)=>ws.reduce((s,w,i)=>s+w*V[i][j],0)));
  return {weights,out,scores};
};

const posEnc = (T,d) => Array.from({length:T},(_,pos)=>Array.from({length:d},(_,i)=>i%2===0?Math.sin(pos/Math.pow(10000,i/d)):Math.cos(pos/Math.pow(10000,(i-1)/d))));

const SENTENCE_SETS = {
  nlp:    ['The','cat','sat','on','the','mat','and','slept'],
  code:   ['def','fn','(','x',')',':','return','x'],
  math:   ['Let','x','=','a','+','b','where','a'],
  custom: ['Token','A','Token','B','Token','C','Token','D'],
};

const TABS = ['attention-map','qkv-flow','multi-head','pos-encoding','transformer-block'];

// ─── ATTENTION HEATMAP ───────────────────────────────────────────────────────
function AttentionHeatmap({weights, tokens, head=0, onClickCell, selectedI=null, selectedJ=null}) {
  if(!weights?.length) return null;
  const N=tokens.length, cellSize=Math.min(52,Math.floor(500/N));
  return (
    <div style={{overflowX:'auto'}}>
      <div style={{display:'inline-block'}}>
        {/* Column headers */}
        <div style={{display:'flex',paddingLeft:cellSize+8}}>
          {tokens.map((t,j)=>(
            <div key={j} style={{width:cellSize,textAlign:'center',fontSize:'10px',color:selectedJ===j?'#fbbf24':'var(--t3)',fontFamily:'JetBrains Mono',fontWeight:selectedJ===j?700:400,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',padding:'0 2px'}}>{t}</div>
          ))}
        </div>
        {weights.map((row,i)=>(
          <div key={i} style={{display:'flex',alignItems:'center',marginBottom:2}}>
            {/* Row header */}
            <div style={{width:cellSize,textAlign:'right',paddingRight:8,fontSize:'10px',color:selectedI===i?'#fbbf24':'var(--t3)',fontFamily:'JetBrains Mono',fontWeight:selectedI===i?700:400,flexShrink:0}}>{tokens[i]}</div>
            {row.map((v,j)=>(
              <div key={j} onClick={()=>onClickCell&&onClickCell(i,j)}
                style={{
                  width:cellSize,height:cellSize,borderRadius:'3px',
                  background:`rgba(34,211,238,${(0.03+v*0.97).toFixed(3)})`,
                  border:selectedI===i&&selectedJ===j?'2px solid #fbbf24':'2px solid transparent',
                  display:'flex',alignItems:'center',justifyContent:'center',
                  fontSize:`${Math.max(7,cellSize*0.22)}px`,
                  color:v>0.35?'#000':'var(--t2)',
                  fontFamily:'JetBrains Mono',cursor:'pointer',boxSizing:'border-box',
                  transition:'transform .1s',
                }}
                title={`${tokens[i]} → ${tokens[j]}: ${v.toFixed(4)}`}>
                {cellSize>=28?v.toFixed(2):''}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── QKV FLOW SVG ────────────────────────────────────────────────────────────
function QKVFlow({Q,K,V,weights,tokens,selectedI=0}) {
  if(!Q?.length) return null;
  const N=tokens.length, VW=700, VH=320;
  const tokenX = Array.from({length:N},(_,i)=>60+i*(VW-80)/(N-1));
  const rowQ=70, rowK=140, rowV=210, rowOut=290;

  return (
    <svg width="100%" viewBox={`0 0 ${VW} ${VH}`} style={{background:'#060b14',borderRadius:'8px',border:'1px solid #1e293b',display:'block'}}>
      {/* Layer labels */}
      {[['QUERY Q',rowQ,'#22d3ee'],['KEY K',rowK,'#4ade80'],['VALUE V',rowV,'#a78bfa'],['OUTPUT',rowOut,'#fbbf24']].map(([lbl,y,c])=>(
        <text key={lbl} x={8} y={y} dominantBaseline="middle" style={{fontSize:'9px',fill:c,fontFamily:'JetBrains Mono',fontWeight:700}}>{lbl}</text>
      ))}

      {/* Connections from selected Q to all K (attention weights) */}
      {weights[selectedI]&&tokens.map((_,j)=>{
        const w=weights[selectedI][j]??0;
        return <line key={j} x1={tokenX[selectedI]} y1={rowQ} x2={tokenX[j]} y2={rowK}
          stroke={`rgba(34,211,238,${0.05+w*0.95})`} strokeWidth={0.5+w*4} opacity={0.8}/>;
      })}

      {/* Connections from K to weighted V sum */}
      {tokens.map((_,j)=>{
        const w=weights[selectedI]?.[j]??0;
        return <line key={j} x1={tokenX[j]} y1={rowK} x2={tokenX[selectedI]} y2={rowV}
          stroke={`rgba(167,139,250,${0.05+w*0.95})`} strokeWidth={0.5+w*3} opacity={0.6}/>;
      })}

      {/* Output */}
      <line x1={tokenX[selectedI]} y1={rowV} x2={tokenX[selectedI]} y2={rowOut-12}
        stroke="#fbbf24" strokeWidth={2} strokeDasharray="4 2"/>

      {/* Tokens at each layer */}
      {tokens.map((tok,i)=>{
        const isSelected=i===selectedI;
        const qv=Math.abs(Q[i]?.[0]??0);
        const kv=Math.abs(K[i]?.[0]??0);
        const vv=Math.abs(V[i]?.[0]??0);
        const aw=weights[selectedI]?.[i]??0;
        return (
          <g key={i}>
            {/* Q node */}
            <circle cx={tokenX[i]} cy={rowQ} r={isSelected?14:10} fill={`rgba(34,211,238,${0.1+qv*0.9})`} stroke={isSelected?'#fbbf24':'#22d3ee'} strokeWidth={isSelected?2:1}/>
            {/* K node */}
            <circle cx={tokenX[i]} cy={rowK} r={10+aw*8} fill={`rgba(74,222,128,${0.1+aw*0.9})`} stroke="#4ade80" strokeWidth={1}/>
            {/* V node */}
            {isSelected&&<circle cx={tokenX[i]} cy={rowV} r={14} fill={`rgba(167,139,250,0.9)`} stroke="#a78bfa" strokeWidth={2}/>}
            {/* Token label */}
            <text x={tokenX[i]} y={rowQ+26} textAnchor="middle" style={{fontSize:'9px',fill:'var(--t3)',fontFamily:'JetBrains Mono'}}>{tok}</text>
            {/* Attention weight on K */}
            {aw>0.05&&<text x={tokenX[i]} y={rowK+1} textAnchor="middle" dominantBaseline="middle" style={{fontSize:'8px',fill:'#000',fontFamily:'JetBrains Mono',fontWeight:700}}>{aw.toFixed(2)}</text>}
          </g>
        );
      })}

      {/* Output value */}
      <text x={tokenX[selectedI]} y={rowOut} textAnchor="middle" dominantBaseline="middle"
        style={{fontSize:'10px',fill:'#fbbf24',fontFamily:'JetBrains Mono',fontWeight:700}}>
        out[{selectedI}]
      </text>
    </svg>
  );
}

export default function AttentionLab() {
  const [tab,setTab]=useState('attention-map');
  const [logs,setLogs]=useState([{ts:'00:00:00',msg:'Attention Lab — compute attention to see which tokens attend to which.',t:'info'}]);
  const log=(msg,t='info')=>setLogs(l=>[...l.slice(-80),{ts:timestamp(),msg,t}]);

  const [sentKey,setSentKey]=useState('nlp');
  const [seqLen,setSeqLen]=useState(6);
  const [dModel,setDModel]=useState(8);
  const [numHeads,setNumHeads]=useState(2);
  const [temp,setTemp]=useState(1.0);
  const [masking,setMasking]=useState(false);
  const [result,setResult]=useState(null);
  const [selectedI,setSelectedI]=useState(0);
  const [selectedJ,setSelectedJ]=useState(null);
  const [selectedHead,setSelectedHead]=useState(0);

  const tokens = SENTENCE_SETS[sentKey].slice(0,seqLen);

  const computeAttention = () => {
    const T=tokens.length, dk=Math.floor(dModel/numHeads);
    const headResults=[];

    for(let h=0;h<numHeads;h++){
      const Q=Array.from({length:T},()=>Array.from({length:dk},()=>randn()*0.5));
      const K=Array.from({length:T},()=>Array.from({length:dk},()=>randn()*0.5));
      const V=Array.from({length:T},()=>Array.from({length:dk},()=>randn()*0.5));

      // Scaled dot-product attention with optional masking
      const scores=Q.map((q,i)=>K.map((k,j)=>{
        const s=dotV(q,k)/(Math.sqrt(dk)*temp);
        return masking&&j>i?-1e9:s;
      }));
      const weights=scores.map(row=>softmax(row));
      const out=weights.map((ws,i)=>V[0].map((_,j)=>ws.reduce((s,w,k)=>s+w*V[k][j],0)));
      headResults.push({Q,K,V,weights,out,scores});
    }

    // Positional encoding
    const pe=posEnc(T,dModel);

    setResult({heads:headResults,tokens,pe,T,dk,dModel,numHeads});
    setSelectedI(0); setSelectedJ(null); setSelectedHead(0);
    const maxAttn=Math.max(...headResults[0].weights.flat());
    log(`Attention computed: ${T} tokens · ${numHeads} heads · d_k=${dk} · temp=${temp}${masking?' [CAUSAL MASK]':''} | max attn=${maxAttn.toFixed(4)}`,'ok');
  };

  const head = result?.heads[selectedHead];

  return (
    <div className="anim-fadeup" style={{display:'flex',flexDirection:'column',gap:'12px'}}>
      <div style={{display:'flex',gap:'3px',background:'var(--deep)',padding:'4px',borderRadius:'3px',border:'1px solid var(--b0)',overflowX:'auto'}}>
        {TABS.map(t=><button key={t} onClick={()=>setTab(t)} style={{padding:'6px 14px',border:`1px solid ${tab===t?AC:'transparent'}`,background:tab===t?AC+'18':'transparent',color:tab===t?AC:'var(--t3)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono',whiteSpace:'nowrap',fontWeight:tab===t?700:400}}>{t.toUpperCase()}</button>)}
      </div>

      {/* Controls */}
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr 1fr 1fr auto',gap:'10px',padding:'12px',background:'var(--surface)',border:'1px solid var(--b0)',borderRadius:'4px',alignItems:'end'}}>
        <Sel label="SENTENCE" value={sentKey} onChange={setSentKey} options={Object.keys(SENTENCE_SETS).map(k=>({value:k,label:k.toUpperCase()}))}/>
        <Sl label="Seq Length" value={seqLen} min={2} max={8} onChange={setSeqLen} color="#22d3ee"/>
        <Sl label="d_model" value={dModel} min={4} max={32} step={4} onChange={setDModel} color="#a78bfa"/>
        <Sl label="Heads" value={numHeads} min={1} max={4} onChange={v=>{setNumHeads(v);setSelectedHead(0);}} color="#fbbf24"/>
        <div>
          <Sl label="Temperature" value={temp} min={0.1} max={3} step={0.1} onChange={setTemp} color="#fb923c"/>
          <div style={{display:'flex',gap:'6px',alignItems:'center'}}>
            <button onClick={()=>setMasking(m=>!m)} style={{padding:'4px 8px',border:`1px solid ${masking?'#f87171':'var(--b0)'}`,background:masking?'#f8717115':'transparent',color:masking?'#f87171':'var(--t3)',fontSize:'9px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono'}}>CAUSAL MASK {masking?'ON':'OFF'}</button>
          </div>
        </div>
        <Btn onClick={computeAttention} color="#22d3ee">COMPUTE →</Btn>
      </div>

      {/* ── ATTENTION MAP TAB ── */}
      {tab==='attention-map'&&(
        <div style={{display:'grid',gridTemplateColumns:'220px 1fr',gap:'12px'}}>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            <Panel title="HEAD SELECTOR" accent={AC}>
              <div style={{display:'flex',gap:'6px',flexWrap:'wrap',marginBottom:'8px'}}>
                {result&&Array.from({length:numHeads},(_,h)=>(
                  <button key={h} onClick={()=>setSelectedHead(h)} style={{padding:'6px 12px',border:`1px solid ${selectedHead===h?P[h%P.length]:'var(--b0)'}`,background:selectedHead===h?P[h%P.length]+'18':'transparent',color:selectedHead===h?P[h%P.length]:'var(--t3)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono',fontWeight:selectedHead===h?700:400}}>
                    Head {h+1}
                  </button>
                ))}
              </div>
              {result&&<div style={{fontSize:'9px',color:'var(--t3)',lineHeight:1.6}}>
                Click any cell to see which query attends to which key.
              </div>}
            </Panel>
            {result&&selectedI!==null&&(
              <Panel title={`TOKEN "${tokens[selectedI]}" ATTENDS TO:`} accent="#fbbf24">
                {head?.weights[selectedI]&&tokens.map((tok,j)=>(
                  <div key={j} style={{marginBottom:'5px'}}>
                    <div style={{display:'flex',justifyContent:'space-between',marginBottom:'2px',fontSize:'9px'}}>
                      <span style={{color:selectedJ===j?'#fbbf24':'var(--t3)',fontFamily:'JetBrains Mono'}}>{tok}</span>
                      <span style={{color:'#22d3ee',fontFamily:'JetBrains Mono',fontWeight:700}}>{head.weights[selectedI][j].toFixed(3)}</span>
                    </div>
                    <div style={{height:6,background:'var(--b0)',borderRadius:3,overflow:'hidden'}}>
                      <div style={{height:'100%',width:`${head.weights[selectedI][j]*100}%`,background:'linear-gradient(90deg,#22d3ee44,#22d3ee)',borderRadius:3}}/>
                    </div>
                  </div>
                ))}
              </Panel>
            )}
            <LogPanel logs={logs} height={90}/>
          </div>

          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            {result
              ? <>
                  <Panel title={`SELF-ATTENTION HEATMAP — HEAD ${selectedHead+1}/${numHeads}${masking?' (CAUSAL MASKED)':''}`} accent={P[selectedHead%P.length]}>
                    <div style={{fontSize:'10px',color:'var(--t3)',marginBottom:'8px'}}>
                      Row i attends to col j. Bright = strong attention. Click a cell to highlight.
                    </div>
                    <AttentionHeatmap weights={head.weights} tokens={tokens} head={selectedHead} onClickCell={(i,j)=>{setSelectedI(i);setSelectedJ(j);}} selectedI={selectedI} selectedJ={selectedJ}/>
                  </Panel>

                  {/* Entropy per token */}
                  <Panel title="ATTENTION ENTROPY — HOW SPREAD IS ATTENTION? (HIGH = DIFFUSE, LOW = FOCUSED)" accent="#fb923c">
                    <ResponsiveContainer width="100%" height={140}>
                      <BarChart data={head.weights.map((row,i)=>{
                        const ent=-row.reduce((s,p)=>s+(p>0?p*Math.log(p+1e-9):0),0);
                        return {token:tokens[i],entropy:+ent.toFixed(3)};
                      })}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                        <XAxis dataKey="token" tick={{fontSize:10,fill:'var(--t3)'}}/>
                        <YAxis tick={{fontSize:9,fill:'var(--t3)'}} width={30}/>
                        <Tooltip/>
                        <Bar dataKey="entropy" name="entropy" radius={[3,3,0,0]}>
                          {head.weights.map((_,i)=><Cell key={i} fill={P[i%P.length]} opacity={0.85}/>)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </Panel>
                </>
              : <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'300px',color:'var(--t3)',fontSize:'12px',border:'1px dashed var(--b1)',borderRadius:'4px'}}>← Hit COMPUTE to see the attention heatmap</div>
            }
          </div>
        </div>
      )}

      {/* ── QKV FLOW TAB ── */}
      {tab==='qkv-flow'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          <div style={{display:'flex',gap:'8px',alignItems:'center',flexWrap:'wrap'}}>
            <span style={{fontSize:'9px',color:'var(--t3)',letterSpacing:'.1em'}}>QUERY TOKEN (which token is "looking"):</span>
            {result&&tokens.map((tok,i)=>(
              <button key={i} onClick={()=>setSelectedI(i)} style={{padding:'4px 12px',border:`1px solid ${selectedI===i?'#fbbf24':'var(--b0)'}`,background:selectedI===i?'#fbbf2415':'transparent',color:selectedI===i?'#fbbf24':'var(--t3)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono',fontWeight:selectedI===i?700:400}}>
                {tok}
              </button>
            ))}
          </div>

          {result
            ? <>
                <Panel title={`QKV FLOW — "${tokens[selectedI]}" QUERYING ALL KEYS — LINE THICKNESS = ATTENTION WEIGHT`} accent="#22d3ee">
                  <QKVFlow Q={head.Q} K={head.K} V={head.V} weights={head.weights} tokens={tokens} selectedI={selectedI}/>
                </Panel>

                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'10px'}}>
                  {[['QUERY Q','#22d3ee',head?.Q],['KEY K','#4ade80',head?.K],['VALUE V','#a78bfa',head?.V]].map(([label,color,mat])=>(
                    <Panel key={label} title={label} accent={color}>
                      <div style={{overflowX:'auto'}}>
                        <table style={{borderCollapse:'collapse',fontSize:'9px',fontFamily:'JetBrains Mono',width:'100%'}}>
                          <thead><tr>{mat?.[0]&&Array.from({length:mat[0].length},(_,d)=><th key={d} style={{padding:'3px 4px',color:'var(--t3)',textAlign:'center'}}>d{d}</th>)}</tr></thead>
                          <tbody>
                            {tokens.map((tok,i)=>(
                              <tr key={i} style={{background:i===selectedI?color+'18':'transparent'}}>
                                <td style={{padding:'3px 4px',color:i===selectedI?'#fbbf24':'var(--t3)',fontWeight:i===selectedI?700:400,whiteSpace:'nowrap'}}>{tok}</td>
                                {mat?.[i]?.map((v,j)=>(
                                  <td key={j} style={{padding:'3px 4px',color,textAlign:'center',background:`${color}${Math.round(Math.abs(v)*40+5).toString(16).padStart(2,'0')}`}}>{v.toFixed(2)}</td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </Panel>
                  ))}
                </div>
              </>
            : <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'300px',color:'var(--t3)',fontSize:'12px',border:'1px dashed var(--b1)',borderRadius:'4px'}}>← Compute attention first</div>
          }
        </div>
      )}

      {/* ── MULTI-HEAD TAB ── */}
      {tab==='multi-head'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          {result
            ? <>
                <div style={{display:'grid',gridTemplateColumns:`repeat(${Math.min(result.numHeads,4)},1fr)`,gap:'10px'}}>
                  {result.heads.map((h,hi)=>(
                    <Panel key={hi} title={`HEAD ${hi+1}  dk=${result.dk}`} accent={P[hi%P.length]}>
                      <AttentionHeatmap weights={h.weights} tokens={tokens} head={hi} onClickCell={(i)=>setSelectedI(i)} selectedI={selectedI}/>
                      <div style={{marginTop:'6px',display:'grid',gridTemplateColumns:'1fr 1fr',gap:'4px'}}>
                        {[['max attn',Math.max(...h.weights.flat()).toFixed(3)],['avg attn',(h.weights.flat().reduce((s,v)=>s+v,0)/h.weights.flat().length).toFixed(3)]].map(([k,v])=>(
                          <div key={k} style={{background:'var(--deep)',padding:'4px 6px',borderRadius:'2px'}}>
                            <div style={{fontSize:'8px',color:'var(--t3)'}}>{k}</div>
                            <div style={{fontSize:'10px',color:P[hi%P.length],fontFamily:'JetBrains Mono',fontWeight:700}}>{v}</div>
                          </div>
                        ))}
                      </div>
                    </Panel>
                  ))}
                </div>
                <Panel title="WHAT EACH HEAD LEARNS TO FOCUS ON" accent="#fbbf24">
                  <div style={{display:'grid',gridTemplateColumns:`repeat(${result.numHeads},1fr)`,gap:'8px'}}>
                    {result.heads.map((h,hi)=>{
                      const maxWeight=Math.max(...h.weights.flat());
                      const argmax=h.weights.reduce((best,row,i)=>{const mj=row.reduce((b,v,j)=>v>row[b]?j:b,0);return h.weights[i][mj]>maxWeight*0.9?{i,j:mj}:best},{i:0,j:0});
                      return (
                        <div key={hi} style={{padding:'10px',background:'var(--deep)',borderRadius:'3px',borderTop:`2px solid ${P[hi%P.length]}`}}>
                          <div style={{fontSize:'10px',color:P[hi%P.length],fontWeight:700,marginBottom:'4px'}}>Head {hi+1}</div>
                          <div style={{fontSize:'9px',color:'var(--t3)',lineHeight:1.6}}>
                            Peak: <span style={{color:'#fbbf24',fontFamily:'JetBrains Mono'}}>"{tokens[argmax.i]}" → "{tokens[argmax.j]}"</span>
                          </div>
                          <div style={{fontSize:'9px',color:'var(--t3)'}}>Max: <span style={{color:P[hi%P.length],fontFamily:'JetBrains Mono'}}>{maxWeight.toFixed(3)}</span></div>
                        </div>
                      );
                    })}
                  </div>
                </Panel>
              </>
            : <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'300px',color:'var(--t3)',fontSize:'12px',border:'1px dashed var(--b1)',borderRadius:'4px'}}>← Compute attention with 2+ heads</div>
          }
          <LogPanel logs={logs} height={90}/>
        </div>
      )}

      {/* ── POSITIONAL ENCODING TAB ── */}
      {tab==='pos-encoding'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          <Panel title="POSITIONAL ENCODING HEATMAP — sin/cos at different frequencies" accent="#fbbf24">
            {result
              ? <div style={{overflowX:'auto'}}>
                  <div style={{display:'inline-block'}}>
                    <div style={{display:'flex',paddingLeft:40}}>
                      {Array.from({length:result.dModel},(_,d)=>(
                        <div key={d} style={{width:24,textAlign:'center',fontSize:'8px',color:'var(--t3)',fontFamily:'JetBrains Mono'}}>d{d}</div>
                      ))}
                    </div>
                    {result.pe.map((row,pos)=>(
                      <div key={pos} style={{display:'flex',alignItems:'center',marginBottom:2}}>
                        <div style={{width:36,textAlign:'right',paddingRight:6,fontSize:'9px',color:'var(--t3)',fontFamily:'JetBrains Mono'}}>{tokens[pos]}</div>
                        {row.map((v,d)=>(
                          <div key={d} title={`pos=${pos} dim=${d}: ${v.toFixed(4)}`}
                            style={{width:24,height:24,background:v>0?`rgba(251,191,36,${0.05+v*0.95})`:`rgba(34,211,238,${0.05+(-v)*0.95})`,borderRadius:2}}>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                  <div style={{display:'flex',gap:'14px',marginTop:'6px',fontSize:'9px',color:'var(--t3)'}}>
                    <span><span style={{color:'#fbbf24'}}>█</span> positive (sin/cos {'>'} 0)</span>
                    <span><span style={{color:'#22d3ee'}}>█</span> negative (sin/cos {'<'} 0)</span>
                  </div>
                </div>
              : <div style={{height:200,display:'flex',alignItems:'center',justifyContent:'center',color:'var(--t3)',fontSize:'12px'}}>← Compute attention first</div>
            }
          </Panel>

          {result&&(
            <Panel title="PE DIMENSIONS — sin/cos WAVES ACROSS POSITIONS" accent="#a78bfa">
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={result.pe.map((row,pos)=>({pos,...Object.fromEntries(row.slice(0,6).map((v,d)=>[`d${d}`,+v.toFixed(4)]))}))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                  <XAxis dataKey="pos" tick={{fontSize:9,fill:'var(--t3)'}} label={{value:'position',fill:'var(--t3)',fontSize:9,position:'insideBottom',offset:-2}}/>
                  <YAxis tick={{fontSize:9,fill:'var(--t3)'}} domain={[-1,1]}/>
                  <Tooltip/>
                  <Legend iconSize={8} wrapperStyle={{fontSize:9}}/>
                  {Array.from({length:Math.min(6,result.dModel)},(_,d)=>(
                    <Line key={d} type="monotone" dataKey={`d${d}`} stroke={P[d%P.length]} dot={{r:3}} strokeWidth={1.5} name={`dim ${d}`}/>
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </Panel>
          )}
        </div>
      )}

      {/* ── TRANSFORMER BLOCK TAB ── */}
      {tab==='transformer-block'&&(
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
          {/* Transformer block diagram */}
          <Panel title="TRANSFORMER BLOCK — ARCHITECTURE" accent="#a78bfa">
            <div style={{display:'flex',flexDirection:'column',gap:'0',alignItems:'center',padding:'8px'}}>
              {[
                {step:'INPUT',desc:'Token embeddings + positional encoding',color:'#60a5fa',arrow:true},
                {step:'MULTI-HEAD\nATTENTION',desc:`${numHeads} heads × d_k=${Math.floor(dModel/numHeads)} each`,color:'#22d3ee',arrow:true},
                {step:'ADD & NORM',desc:'Residual: x + Attn(x), then LayerNorm',color:'#4ade80',arrow:true},
                {step:'FFN',desc:`2-layer MLP: d=${dModel} → ${dModel*4} → ${dModel}  (GELU)`,color:'#fbbf24',arrow:true},
                {step:'ADD & NORM',desc:'Residual: x + FFN(x), then LayerNorm',color:'#4ade80',arrow:true},
                {step:'OUTPUT',desc:'To next block or softmax head',color:'#fb923c',arrow:false},
              ].map((l,i)=>(
                <div key={i} style={{display:'flex',flexDirection:'column',alignItems:'center',width:'100%'}}>
                  <div style={{width:'80%',padding:'10px 16px',background:'var(--deep)',borderRadius:'4px',borderLeft:`3px solid ${l.color}`,textAlign:'center'}}>
                    <div style={{fontSize:'11px',color:l.color,fontWeight:700,marginBottom:'3px',whiteSpace:'pre-line'}}>{l.step}</div>
                    <div style={{fontSize:'9px',color:'var(--t2)'}}>{l.desc}</div>
                  </div>
                  {l.arrow&&<div style={{width:2,height:14,background:'#334155'}}/>}
                </div>
              ))}
            </div>
          </Panel>

          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            <Panel title="COMPLEXITY COMPARISON" accent="#fb923c">
              <div style={{fontSize:'10px',color:'var(--t3)',marginBottom:'10px'}}>Self-attention vs sequential models</div>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:'10px',fontFamily:'JetBrains Mono'}}>
                <thead><tr style={{background:'var(--deep)'}}>
                  {['','Attention','RNN','CNN'].map(h=><th key={h} style={{padding:'6px 8px',textAlign:'left',color:'var(--t3)',fontSize:'9px',borderBottom:'1px solid var(--b0)'}}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {[
                    ['Seq. ops per layer','O(1)','O(n)','O(n/k)'],
                    ['Path length','O(1)','O(n)','O(log_k n)'],
                    ['Complexity','O(n²·d)','O(n·d²)','O(k·n·d²)'],
                    ['Parallelizable','✓ Yes','✗ No','✓ Yes'],
                    ['Long-range','✓ Easy','✗ Hard','~ Medium'],
                  ].map(([k,...vals])=>(
                    <tr key={k} style={{borderBottom:'1px solid var(--b0)'}}>
                      <td style={{padding:'5px 8px',color:'var(--t3)',fontSize:'9px'}}>{k}</td>
                      {vals.map((v,i)=><td key={i} style={{padding:'5px 8px',color:P[i],textAlign:'center'}}>{v}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </Panel>

            <Panel title="KEY INNOVATIONS IN TRANSFORMER" accent="#22d3ee">
              {[
                {name:'Scaled dot-product',eq:'softmax(QKᵀ/√dk)·V',why:'Prevents saturation in softmax',color:'#22d3ee'},
                {name:'Multi-head',eq:'Concat(head₁...headₕ)·Wo',why:'Each head learns different patterns',color:'#a78bfa'},
                {name:'Positional encoding',eq:'PE(pos,2i) = sin(pos/10000^(2i/d))',why:'Injects position without recurrence',color:'#fbbf24'},
                {name:'Residual + LayerNorm',eq:'x + SubLayer(LayerNorm(x))',why:'Enables very deep networks to train',color:'#4ade80'},
              ].map(({name,eq,why,color})=>(
                <div key={name} style={{padding:'8px',background:'var(--deep)',borderRadius:'3px',marginBottom:'6px',borderLeft:`2px solid ${color}`}}>
                  <div style={{fontSize:'10px',color,fontWeight:700,marginBottom:'2px'}}>{name}</div>
                  <div style={{fontSize:'9px',color:'var(--t2)',fontFamily:'JetBrains Mono',marginBottom:'3px'}}>{eq}</div>
                  <div style={{fontSize:'9px',color:'var(--t3)'}}>{why}</div>
                </div>
              ))}
            </Panel>
          </div>
        </div>
      )}
    </div>
  );
}
